import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import { IconForth16 } from '@cineamo/ui/src/icons/ic-forth';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconUp16 } from '@cineamo/ui/src/icons/ic-up';
import { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import CartSummary from './CartSummary';
import { MINIMUM_AMOUNT_DUE } from './GiftCardRedemption';
import SeatMapLegend, { type SeatMapLegendFurniture } from './SeatMapLegend';

const CheckoutButton: React.FC<{
    onCheckoutClick: () => void;
    /** The room's seats, for the list of the selected ones in the footer's details. */
    seats: Seat[];
    seatCategories: SeatCategory[];
    hasTables: boolean;
    hasWheelchairSeats: boolean;
    furniture?: SeatMapLegendFurniture;
    isLoading: boolean;
}> = ({ onCheckoutClick, seats, seatCategories, hasTables, hasWheelchairSeats, furniture, isLoading }) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const {
        selectedSeats,
        total: totalPrice,
        giftCardDeduction,
        amountDue,
        currency,
        currentStep,
        hasNoPriceOptions
    } = useCartProvider();
    const hideButton = !['01_seat_picking', '01.5_cart', '02_payment'].includes(currentStep);
    // The seat picking step prices the cart client-side, ahead of the sync that lets the API price the gift card.
    const hasGiftCard = giftCardDeduction > 0 && currentStep !== '01_seat_picking';
    const totalAmount = formatter.number.formatCurrency(totalPrice / 100, currency);
    const amount = hasGiftCard ? formatter.number.formatCurrency(amountDue / 100, currency) : totalAmount;
    const isFullyCovered = hasGiftCard && amountDue === 0;
    const buttonText = {
        '01_seat_picking': t('continue'),
        '01.5_cart': isFullyCovered ? t('complete-booking') : t('continue-with-payment'),
        '02_payment': t('pay-now', { amount })
    }[currentStep as '01_seat_picking' | '01.5_cart' | '02_payment'];

    const buttonStyle = {
        '02_payment': 'lg:hidden'
    }[currentStep as '02_payment'];

    const isAmountDueTooLow = hasGiftCard && amountDue > 0 && amountDue < MINIMUM_AMOUNT_DUE;
    const isDisabled =
        isLoading ||
        hasNoPriceOptions ||
        selectedSeats.length <= 0 ||
        (currentStep === '01.5_cart' && isAmountDueTooLow);

    // Small screens, seat picking: the selected seats and the price category hint, which would sit below the map,
    // fold out of the footer. Its toggle shows only once a seat is picked, when there is something to show.
    const hasDetails = currentStep === '01_seat_picking' && selectedSeats.length > 0;
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    // Closed with the last seat, so picking a seat again does not lay them over the map unasked.
    if (isDetailsOpen && selectedSeats.length === 0) setIsDetailsOpen(false);
    const isDetailsShown = hasDetails && isDetailsOpen;

    // The sidebar keeps this much space free at the page's end on small screens, where the footer is fixed over it.
    const footerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const footer = footerRef.current;
        if (!footer) return;
        const root = document.documentElement;
        const observer = new ResizeObserver(() =>
            root.style.setProperty('--booking-footer-height', `${footer.offsetHeight}px`)
        );
        observer.observe(footer);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--booking-footer-height');
        };
    }, []);

    const containerClassName = cn(
        'left-0 right-0',
        'fixed lg:sticky',
        'z-[900] w-full',
        'bottom-0',
        'overflow-clip rounded-t-16 lg:rounded-t-8',
        'bg-[var(--background-color)] text-[var(--on-background-color)]',
        'lg:bg-[var(--non-transparent-surface-color)] lg:text-[var(--on-non-transparent-surface-color)]'
    );

    if (hasNoPriceOptions) {
        return (
            <div className={containerClassName}>
                <p
                    role="alert"
                    className="p-16 text-body-bold-14">
                    {t('booking-unavailable')}
                </p>
            </div>
        );
    }

    return (
        <div className={containerClassName}>
            {isDetailsShown && (
                <div
                    id="booking-footer-details"
                    className="lg:hidden">
                    <CartSummary
                        seats={seats}
                        className="max-h-[40svh] overflow-y-auto pt-16"
                    />
                </div>
            )}
            {/* Measured without the details: opened, they lie over the map instead of shrinking it. */}
            <div
                ref={footerRef}
                data-booking-footer>
                {/* Mobile, as in the booking design: the legend, then seats, total and the continue button in one row. */}
                <div className="flex flex-col lg:hidden">
                    {hasDetails && (
                        <button
                            type="button"
                            onClick={() => setIsDetailsOpen((value) => !value)}
                            aria-expanded={isDetailsShown}
                            aria-controls={isDetailsShown ? 'booking-footer-details' : undefined}
                            className="flex items-center justify-center gap-4 px-[9px] py-[10px] text-body-bold-12 outline-none focus-visible:underline">
                            {t(isDetailsShown ? 'hide-details' : 'show-details')}
                            {isDetailsShown ? <IconDown16 fill="currentColor" /> : <IconUp16 fill="currentColor" />}
                        </button>
                    )}
                    {currentStep === '01_seat_picking' && (
                        <SeatMapLegend
                            renderSeatTypes={{
                                unavailable: true,
                                selected: true,
                                wheelchair: hasWheelchairSeats,
                                table: hasTables,
                                ...furniture
                            }}
                            seatCategories={seatCategories}
                            className={cn('px-[9px] pb-4 pt-[10px]', hasDetails && 'pt-0')}
                        />
                    )}
                    <div className="flex items-center gap-16 px-[9px] py-16">
                        <div className="flex flex-col">
                            <span className="text-body-12 font-montserrat-medium">{t('selected-seats-short')}</span>
                            <span className="text-headline-20">{selectedSeats.length}</span>
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col">
                            <span className="text-body-12 font-montserrat-medium">
                                {hasGiftCard ? t('gift-card.amount-due') : t('total-incl-tax')}
                            </span>
                            <span className="text-headline-20">{amount}</span>
                        </div>
                        {!hideButton && (
                            <button
                                type="button"
                                onClick={onCheckoutClick}
                                disabled={isDisabled}
                                className={cn(
                                    'flex shrink-0 items-center justify-center gap-8 rounded-8 px-16 py-10 text-body-bold-14',
                                    'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                                    'outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                                    'disabled:cursor-not-allowed disabled:opacity-50'
                                )}>
                                {isLoading && <IconLoading16 className="animate-spin" />}
                                {buttonText}
                                <IconForth16 fill="currentColor" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="hidden w-full flex-col space-y-8 p-16 lg:flex">
                    <div className="flex flex-row space-x-16 justify-between items-center">
                        <div className="flex flex-col space-y-4">
                            <span className="text-body-bold-16">{t('total')}</span>
                            <span className="text-body-14 font-montserrat-medium opacity-50">
                                {t('incl-fees-and-taxes')}
                            </span>
                        </div>
                        <span className="text-body-bold-16">{totalAmount}</span>
                    </div>
                    {hasGiftCard && (
                        <>
                            <div className="flex flex-row space-x-16 justify-between items-center text-body-14">
                                <span>{t('gift-card.applied-deduction')}</span>
                                <span>{formatter.number.formatCurrency(-giftCardDeduction / 100, currency)}</span>
                            </div>
                            <div className="flex flex-row space-x-16 justify-between items-center">
                                <span className="text-body-bold-16">{t('gift-card.amount-due')}</span>
                                <span className="text-body-bold-16">{amount}</span>
                            </div>
                        </>
                    )}
                    {!hideButton && (
                        <Button
                            className={cn('w-full min-h-48', buttonStyle)}
                            style={ButtonStyle.default}
                            onClick={onCheckoutClick}
                            disabled={isDisabled}
                            leadingIcon={isLoading && <IconLoading16 className="animate-spin" />}>
                            {buttonText}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckoutButton;
